/* Auro: basket helpers for the Dawn 16 cart drawer and basket page.
   1. <auro-bundle-remove>: one "Remove bundle" button per bundle group. Removes every line of the bundle in one
      /cart/update.js request (quantity 0 per line key), then re-renders the same sections Dawn re-renders after a
      quantity change, and publishes PUB_SUB_EVENTS.cartUpdate so the header count and auro-cards.js stay in step.
   2. <auro-basket-addons>: the "Goes well with" row in the drawer. Fetches Shopify complementary recommendations
      for one product through the auro-basket-addons section. If the product has none set (Search & Discovery app),
      it falls back to Shopify's related products. Shows nothing when there are neither.
      Add uses /cart/add.js and refreshes the drawer the Dawn way (cart-drawer renderContents).
   3. Basket resync after a failed add. Shopify can answer an add with an error and still add part of it
      ("Only 4 items were added to your cart due to availability"). Dawn then shows the message but leaves the
      drawer blank and the header count stale, so the drawer and count are re-rendered from the real basket.
   4. Free journal offer (snippets/auro-journal-offer.liquid): the "Add free" button adds the £0 journal the same
      way as the add-ons in part 2. On the basket page, Dawn's cart-items refreshes itself from the cartUpdate event.
   Loaded by snippets/cart-drawer.liquid and sections/main-cart-items.liquid (guarded so it only runs once). */
(() => {
  if (window.__auroBasket) return;
  window.__auroBasket = true;

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const jsonPost = () => (typeof fetchConfig === 'function' ? fetchConfig() : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } });
  const updateUrl = () => (window.routes && window.routes.cart_update_url) || root() + 'cart/update';
  const addUrl = () => (window.routes && window.routes.cart_add_url) || root() + 'cart/add';
  const errorText = () => (window.cartStrings && window.cartStrings.error) || 'There was an error while updating your basket. Please try again.';
  /* PUB_SUB_EVENTS is a top-level const in constants.js, so it is a global but not a property of window. */
  const hasPubSub = () => typeof PUB_SUB_EVENTS !== 'undefined';
  const announce = (data, source) => {
    if (typeof publish === 'function' && hasPubSub()) publish(PUB_SUB_EVENTS.cartUpdate, { source, cartData: data });
  };
  const parseSection = (html, selector) => {
    const found = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return found ? found.innerHTML : null;
  };

  /* ---------- 1. Remove a whole bundle ---------- */
  const removeBundle = (host, button) => {
    const keys = String(host.dataset.lineKeys || '').split(',').filter(Boolean);
    const cartItems = host.closest('cart-items') || host.closest('cart-drawer-items');
    if (!keys.length || !cartItems || typeof cartItems.getSectionsToRender !== 'function') return;
    if (button.disabled) return;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    const updates = {};
    keys.forEach((key) => { updates[key] = 0; });
    /* Read the section list before the request, as Dawn does, while the elements are still in the page. */
    const sections = cartItems.getSectionsToRender();
    const list = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (list) list.classList.add('cart__items--disabled');

    /* Dawn standard event, so analytics see the same thing as a normal remove. */
    let deferred = null;
    try {
      const { CartLinesUpdateEvent } = window.StandardEvents || {};
      if (CartLinesUpdateEvent) {
        deferred = CartLinesUpdateEvent.createPromise();
        cartItems.dispatchEvent(new CartLinesUpdateEvent({ action: 'remove', context: 'cart', lines: keys.map((id) => ({ id, quantity: 0 })), promise: deferred.promise }));
      }
    } catch (error) { deferred = null; }

    const body = JSON.stringify({ updates, sections: sections.map((s) => s.section), sections_url: window.location.pathname });
    return fetch(updateUrl(), { ...jsonPost(), body })
      .then((response) => response.json())
      .then((state) => {
        if (!state || state.errors || state.status) throw new Error((state && (state.description || state.errors)) || errorText());
        try {
          const { CartLinesUpdateEvent } = window.StandardEvents || {};
          if (deferred && CartLinesUpdateEvent) deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(state) });
        } catch (error) { /* analytics only */ }

        const empty = state.item_count === 0;
        cartItems.classList.toggle('is-empty', empty);
        const drawer = document.querySelector('cart-drawer');
        const footer = document.getElementById('main-cart-footer');
        if (footer) footer.classList.toggle('is-empty', empty);
        if (drawer) drawer.classList.toggle('is-empty', empty);

        sections.forEach((section) => {
          const hostEl = document.getElementById(section.id);
          const html = state.sections && state.sections[section.section];
          if (!hostEl || !html) return;
          const target = hostEl.querySelector(section.selector) || hostEl;
          const inner = parseSection(html, section.selector);
          if (inner !== null) target.innerHTML = inner;
        });

        /* Focus, as Dawn does after a remove. */
        if (drawer && typeof trapFocus === 'function') {
          if (empty && drawer.querySelector('.drawer__inner-empty')) trapFocus(drawer.querySelector('.drawer__inner-empty'), drawer.querySelector('a'));
          else if (drawer.classList.contains('active') && drawer.querySelector('.cart-item__name')) trapFocus(drawer, drawer.querySelector('.cart-item__name'));
        }
        const status = document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
        if (status) status.textContent = 'Bundle removed from your basket';

        /* Same source as Dawn's own quantity change: the basket UI is already up to date. */
        announce(state, 'cart-items');
      })
      .catch((error) => {
        if (deferred) deferred.reject(error);
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = errorText();
        button.disabled = false;
        button.removeAttribute('aria-busy');
      })
      .finally(() => {
        const after = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
        if (after) after.classList.remove('cart__items--disabled');
        if (list && list !== after) list.classList.remove('cart__items--disabled');
      });
  };

  if (!customElements.get('auro-bundle-remove')) {
    customElements.define('auro-bundle-remove', class AuroBundleRemove extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click', (event) => {
          const button = event.target.closest('button');
          if (!button) return;
          event.preventDefault();
          removeBundle(this, button);
        });
      }
    });
  }

  /* ---------- 2. Goes well with ---------- */
  const addonCache = new Map();

  if (!customElements.get('auro-basket-addons')) {
    customElements.define('auro-basket-addons', class AuroBasketAddons extends HTMLElement {
      constructor() {
        super();
        this.addEventListener('click', (event) => {
          const button = event.target.closest('[data-auro-addon-add]');
          if (button) { event.preventDefault(); this.add(button); }
        });
      }

      connectedCallback() {
        const id = this.dataset.productId;
        if (!id) { this.hidden = true; return; }
        /* The cache key includes what is in the basket, because the section leaves out products already in it. */
        const cacheKey = id + '|' + (this.dataset.cartSig || '');
        if (addonCache.has(cacheKey)) { this.show(addonCache.get(cacheKey)); return; }
        const base = this.dataset.url || root() + 'recommendations/products';
        const sectionId = encodeURIComponent(this.dataset.sectionId || 'auro-basket-addons');
        const load = (intent) =>
          fetch(`${base}?product_id=${encodeURIComponent(id)}&limit=3&intent=${intent}&section_id=${sectionId}`)
            .then((response) => (response.ok ? response.text() : ''))
            .then((text) => {
              if (!text) return '';
              const ready = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-auro-addons-ready]');
              return ready ? ready.outerHTML : '';
            });
        /* Complementary products first; related products when none have been set for this product. */
        load('complementary')
          .then((html) => html || load('related'))
          .then((html) => {
            addonCache.set(cacheKey, html);
            if (this.isConnected) this.show(html);
          })
          .catch(() => { this.hidden = true; });
      }

      show(html) {
        this.innerHTML = html || '';
        this.hidden = !html;
      }

      add(button) {
        if (button.disabled) return;
        const msg = this.querySelector('[data-auro-addon-msg]');
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        const drawer = document.querySelector('cart-drawer');
        const sections = drawer && typeof drawer.getSectionsToRender === 'function' ? drawer.getSectionsToRender().map((s) => s.id) : ['cart-drawer', 'cart-icon-bubble'];
        const body = JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: 1 }], sections, sections_url: window.location.pathname });
        fetch(addUrl(), { ...jsonPost(), body })
          .then((response) => response.json())
          .then((state) => {
            if (!state || state.status || state.errors) throw new Error((state && (state.description || state.message)) || errorText());
            if (drawer && typeof drawer.renderContents === 'function' && state.sections) drawer.renderContents(state);
            /* Same source as Dawn's product form, so the basket page (if open) and auro-cards.js also refresh. */
            announce(state, 'product-form');
          })
          .catch((error) => {
            button.disabled = false;
            button.removeAttribute('aria-busy');
            if (msg) { msg.textContent = error.message || errorText(); msg.hidden = false; }
          });
      }
    });
  }

  /* ---------- 3. Resync the drawer and header count after a failed add ---------- */
  const resyncBasket = () => {
    const drawer = document.querySelector('cart-drawer');
    const cartUrl = (window.routes && window.routes.cart_url) || root() + 'cart';
    return fetch(`${cartUrl}?sections=cart-drawer,cart-icon-bubble`)
      .then((response) => response.json())
      .then((sections) => {
        const drawerDoc = sections['cart-drawer'] ? new DOMParser().parseFromString(sections['cart-drawer'], 'text/html') : null;
        const fresh = drawerDoc && drawerDoc.querySelector('cart-drawer');
        const freshInner = drawerDoc && drawerDoc.querySelector('#CartDrawer');
        const current = document.getElementById('CartDrawer');
        if (drawer && fresh && freshInner && current) {
          current.innerHTML = freshInner.innerHTML;
          drawer.classList.toggle('is-empty', fresh.classList.contains('is-empty'));
          const overlay = drawer.querySelector('#CartDrawer-Overlay');
          if (overlay && typeof drawer.close === 'function') overlay.addEventListener('click', drawer.close.bind(drawer));
        }
        const bubble = document.getElementById('cart-icon-bubble');
        const freshBubble = sections['cart-icon-bubble'] ? parseSection(sections['cart-icon-bubble'], '.shopify-section') : null;
        if (bubble && freshBubble !== null) bubble.innerHTML = freshBubble;
      })
      .catch(() => { /* the page still works; a reload shows the real basket */ });
  };

  if (typeof subscribe === 'function' && hasPubSub() && PUB_SUB_EVENTS.cartError) {
    subscribe(PUB_SUB_EVENTS.cartError, (event) => {
      if (!event || event.source !== 'product-form') return;
      /* Wait a moment: Dawn's product form removes the drawer's is-empty class after it reports the error. */
      window.setTimeout(resyncBasket, 150);
    });
  }

  /* ---------- 4. Free journal offer ---------- */
  /* Delegated from the document: the card is replaced every time the drawer or basket page re-renders. */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-auro-journal-add]');
    if (!button || button.disabled) return;
    event.preventDefault();
    const card = button.closest('.auro-jo');
    const msg = card && card.querySelector('[data-auro-journal-msg]');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    const drawer = document.querySelector('cart-drawer');
    const inDrawer = !!(drawer && button.closest('cart-drawer'));
    const sections = inDrawer && typeof drawer.getSectionsToRender === 'function' ? drawer.getSectionsToRender().map((s) => s.id) : ['cart-drawer', 'cart-icon-bubble'];
    const body = JSON.stringify({ items: [{ id: Number(button.dataset.variantId), quantity: 1 }], sections, sections_url: window.location.pathname });
    fetch(addUrl(), { ...jsonPost(), body })
      .then((response) => response.json())
      .then((state) => {
        if (!state || state.status || state.errors) throw new Error((state && (state.description || state.message)) || errorText());
        if (drawer && typeof drawer.renderContents === 'function' && state.sections && state.sections['cart-drawer']) drawer.renderContents(state);
        /* The basket page's cart-items re-renders itself on this event, which also removes the card there. */
        announce(state, 'product-form');
      })
      .catch((error) => {
        button.disabled = false;
        button.removeAttribute('aria-busy');
        if (msg) { msg.textContent = error.message || errorText(); msg.hidden = false; }
      });
  });
})();